class MediaLifecycleRegistry {
  constructor() {
    this.records = new Map();
    this.listeners = new Set();
  }

  emit(detail) {
    this.listeners.forEach((listener) => listener(detail));
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  register(element, options = {}) {
    if (!element) return () => {};
    this.unregister(element);
    const mediaID = options.id || element.id || 'unknown-media';
    const type = options.type || element.tagName?.toLowerCase() || 'media';
    const emitState = (state, message) => this.emit({ mediaID, type, state, message });
    const handlers = {
      loadstart: () => emitState('loading'),
      waiting: () => emitState('buffering'),
      stalled: () => emitState('buffering'),
      canplay: () => emitState('ready'),
      playing: () => emitState('ready'),
      emptied: () => emitState('loading'),
      abort: () => emitState('ready'),
      error: () => emitState('error', options.errorMessage || `The ${type} could not be loaded.`),
    };

    Object.entries(handlers).forEach(([eventName, handler]) => {
      element.addEventListener(eventName, handler);
    });
    this.records.set(element, { handlers, mediaID, type });
    return () => this.unregister(element);
  }

  report(elementOrID, state, message) {
    const record = this.records.get(elementOrID);
    const mediaID = record?.mediaID || elementOrID?.id || elementOrID || 'unknown-media';
    const type = record?.type || elementOrID?.tagName?.toLowerCase() || 'media';
    this.emit({ mediaID, type, state, message });
  }

  unregister(element) {
    const record = this.records.get(element);
    if (!record) return;
    Object.entries(record.handlers).forEach(([eventName, handler]) => {
      element.removeEventListener(eventName, handler);
    });
    this.records.delete(element);
    this.emit({ mediaID: record.mediaID, type: record.type, state: 'removed' });
  }

  dispose(element) {
    if (!element) return;
    this.unregister(element);
    try {
      element.pause?.();
      element.removeAttribute?.('src');
      element.querySelectorAll?.('source').forEach((source) => source.removeAttribute('src'));
      element.load?.();
      element.remove?.();
    } catch (error) {
      element.remove?.();
    }
  }

  disposeAll() {
    [...this.records.keys()].forEach((element) => this.dispose(element));
  }

  retry(elements, timeout = 5000) {
    const uniqueElements = [...new Set(elements || [])]
      .filter((element) => typeof element?.load === 'function');
    if (!uniqueElements.length) return Promise.resolve([]);

    return Promise.all(uniqueElements.map((element) => new Promise((resolve) => {
      let settled = false;
      const finish = (state) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        element.removeEventListener('canplay', ready);
        element.removeEventListener('error', failed);
        resolve({ element, state });
      };
      const ready = () => finish('ready');
      const failed = () => finish('error');
      const timer = setTimeout(() => finish('timeout'), timeout);
      element.addEventListener('canplay', ready, { once: true });
      element.addEventListener('error', failed, { once: true });
      this.report(element, 'loading');
      element.load();
    })));
  }
}

export const mediaLifecycle = new MediaLifecycleRegistry();
export const registerMediaElement = (element, options) => mediaLifecycle.register(element, options);
export const reportMediaLifecycle = (elementOrID, state, message) => mediaLifecycle.report(elementOrID, state, message);
export const subscribeMediaLifecycle = (listener) => mediaLifecycle.subscribe(listener);
export const retryMediaElements = (elements, timeout) => mediaLifecycle.retry(elements, timeout);
export const disposeMediaElement = (element) => mediaLifecycle.dispose(element);
export const disposeAllManagedMedia = () => mediaLifecycle.disposeAll();
